import { Stack, Typography } from '@mui/material';
import { Control, Controller, FieldValues, Path } from 'react-hook-form';
import { getQuestionnaireItemFieldKey } from './getQuestionnaireItemFieldKey';
import {
    isQuestionnaireItemRequired,
    QuestionnaireFormData,
    questionnaireAnswerIsMissing,
} from './QuestionnaireCRUD.utils';
import { QuestionnaireItemAnswerField } from './QuestionnaireItemAnswerField';

export type QuestionnaireResponseFormProps<TFieldValues extends FieldValues> = {
    questionnaire: QuestionnaireFormData;
    control: Control<TFieldValues>;
    /** react-hook-form path prefix, e.g. `questionnaires.0` */
    namePrefix: Path<TFieldValues>;
};

function QuestionnaireResponseItemField<TFieldValues extends FieldValues>({
    item,
    itemIndex,
    control,
    namePrefix,
}: {
    item: QuestionnaireFormData['items'][number];
    itemIndex: number;
    control: Control<TFieldValues>;
    namePrefix: Path<TFieldValues>;
}) {
    const fieldKey = getQuestionnaireItemFieldKey(itemIndex);
    const fieldName = `${namePrefix}.answers.${fieldKey}` as Path<TFieldValues>;

    if (item.type === 'Label') {
        return <QuestionnaireItemAnswerField item={item} value={undefined} onChange={() => {}} fieldKey={fieldKey} />;
    }

    return (
        <Controller
            name={fieldName}
            control={control}
            rules={{
                validate: (value) => {
                    if (!isQuestionnaireItemRequired(item)) {
                        return true;
                    }
                    if (questionnaireAnswerIsMissing(value)) {
                        return 'This field is required';
                    }
                    return true;
                },
            }}
            render={({ field, fieldState }) => (
                <QuestionnaireItemAnswerField
                    item={item}
                    value={field.value}
                    onChange={field.onChange}
                    error={fieldState.invalid}
                    helperText={fieldState.error?.message}
                    fieldKey={fieldKey}
                />
            )}
        />
    );
}

/**
 * Supplier-facing questionnaire form (read-only structure, editable answers).
 * Uses the same item types and controls as {@link QuestionnaireBuilder}.
 */
export function QuestionnaireResponseForm<TFieldValues extends FieldValues>({
    questionnaire,
    control,
    namePrefix,
}: QuestionnaireResponseFormProps<TFieldValues>) {
    return (
        <Stack spacing={3}>
            <Stack spacing={0.5}>
                <Typography variant="subtitle1">{questionnaire.name}</Typography>
                {questionnaire.description ? (
                    <Typography variant="body2" color="text.secondary">
                        {questionnaire.description}
                    </Typography>
                ) : null}
            </Stack>
            <Stack spacing={3} pl={1}>
                {questionnaire.items.map((item, itemIndex) => (
                    <QuestionnaireResponseItemField
                        key={`${namePrefix}-${getQuestionnaireItemFieldKey(itemIndex)}`}
                        item={item}
                        itemIndex={itemIndex}
                        control={control}
                        namePrefix={namePrefix}
                    />
                ))}
            </Stack>
        </Stack>
    );
}
